import { createEffect, createMemo, createSignal, For, on, onCleanup, onMount, Show } from "solid-js";
import { useLocation } from "@solidjs/router";
import Text from "./../components/Text";
import ResourceAccessPanel from "../components/ResourceAccessPanel";
import { EmptyState } from "../components/EmptyState";
import { Badge } from "../components/Badge";
import { Skeleton } from "../components/Skeleton";
import { useToast } from "../components/MessageBox";
import { useTranslation } from "../hooks/useTranslation";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { STORAGE_ACTIONS } from "../services/resourceActions";
import { useInventoryApi, type StorageBotAssignmentInfo, type StorageInfo } from "../services/inventoryApi";
import StorageList from "../components/inventory/StorageList";
import StorageOverviewTab from "./storage/StorageOverviewTab";
import StorageChestsTab from "./storage/StorageChestsTab";
import StorageStockTab from "./storage/StorageStockTab";
import StorageTasksTab from "./storage/StorageTasksTab";
import StorageActivityTab from "./storage/StorageActivityTab";
import styles from "./StorageAdmin.module.css";

type TabKey = "overview" | "chests" | "stock" | "tasks" | "access" | "activity";

const TAB_KEYS: TabKey[] = ["overview", "chests", "stock", "tasks", "access", "activity"];

function readHash(): { storageId: string | null; tab: TabKey | null } {
  const [storageId, tab] = window.location.hash.replace(/^#/, "").split("/");
  return {
    storageId: storageId || null,
    tab: TAB_KEYS.includes(tab as TabKey) ? (tab as TabKey) : null,
  };
}

// replaceState rather than assigning location.hash: picking a tab is not a navigation, and one
// history entry per click would make the browser's Back button walk the tab bar instead of leaving
// the page. It also leaves ?preview=true and any other query alone.
function writeHash(storageId: string | null, tab: TabKey) {
  const suffix = storageId ? `#${storageId}/${tab}` : "";
  window.history.replaceState(
    window.history.state, "", `${window.location.pathname}${window.location.search}${suffix}`);
}

export default function StorageAdmin() {
  const { t } = useTranslation();
  const toast = useToast();
  const inv = useInventoryApi();
  const stockPlanningEnabled = useFeatureFlag("STOCK_PLANNING");
  const routerLocation = useLocation();

  const [storages, setStorages] = createSignal<StorageInfo[]>([]);
  const [loading, setLoading] = createSignal(true);
  const [selectedId, setSelectedId] = createSignal<string | null>(null);
  const [activeTab, setActiveTab] = createSignal<TabKey>("overview");
  const [editing, setEditing] = createSignal(false);
  const [assignedBots, setAssignedBots] = createSignal<StorageBotAssignmentInfo[]>([]);
  const [activityCount, setActivityCount] = createSignal<number | undefined>(undefined);

  const selectedStorage = createMemo(() => storages().find(s => s.id === selectedId()) ?? null);
  const assignedBotIds = createMemo(() => assignedBots().map(b => b.botRegistryId));

  const may = (action: string) => selectedStorage()?.myActions.includes(action) ?? false;

  onMount(() => {
    void (async () => {
      await loadStorages(true);
      await restoreFromHash();
    })();

    const onHashChange = () => void restoreFromHash();
    window.addEventListener("hashchange", onHashChange);
    onCleanup(() => window.removeEventListener("hashchange", onHashChange));
  });

  // A bell link opened while this page is already showing navigates through the router, which fires
  // no hashchange.
  createEffect(on(() => routerLocation.hash, () => void restoreFromHash(), { defer: true }));

  // Reopens whatever the hash names, so a reload or a shared link lands on the same storage and tab
  // instead of the empty "pick a storage" state.
  async function restoreFromHash() {
    const { storageId, tab } = readHash();
    if (!storageId || !storages().some(s => s.id === storageId)) return;
    if (storageId === selectedId() && (tab === null || tab === activeTab())) return;

    setSelectedId(storageId);
    setEditing(false);
    setActivityCount(undefined);
    // A tab the current user cannot see (access, without the permission) is not in tabs(), so falling
    // back to overview beats rendering a panel the tab bar does not offer.
    setActiveTab(tab && tabs().some(candidate => candidate.key === tab) ? tab : "overview");
    await loadAssignedBots(storageId);
  }

  function selectTab(tab: TabKey) {
    setActiveTab(tab);
    writeHash(selectedId(), tab);
  }

  async function loadStorages(initial = false) {
    try {
      setStorages(await inv.fetchStorages());
    } catch (e: any) {
      toast?.error(e?.message ?? t("STORAGE_LOAD_FAILED"));
    } finally {
      if (initial) setLoading(false);
    }
  }

  async function loadAssignedBots(storageId: string) {
    try {
      setAssignedBots(await inv.fetchStorageBots(storageId));
    } catch (e: any) {
      setAssignedBots([]);
      toast?.error(e?.message ?? t("STORAGE_BOTS_LOAD_FAILED"));
    }
  }

  async function handleSelect(id: string) {
    setSelectedId(id);
    setEditing(false);
    setActiveTab("overview");
    setActivityCount(undefined);
    writeHash(id, "overview");
    await loadAssignedBots(id);
  }

  async function handleCreate() {
    try {
      const created = await inv.createStorage(t("STORAGE_NEW_STORAGE_NAME"));
      await loadStorages();
      await handleSelect(created.id);
      setEditing(true);
    } catch (e: any) {
      toast?.error(e?.message ?? t("STORAGE_CREATE_FAILED"));
    }
  }

  function handleDeleted() {
    setSelectedId(null);
    setEditing(false);
    writeHash(null, "overview");
    void loadStorages();
  }

  const tabs = createMemo((): { key: TabKey; label: string; count?: number }[] => [
    { key: "overview", label: "STORAGE_TAB_OVERVIEW" },
    { key: "chests", label: "STORAGE_CHESTS", count: selectedStorage()?.chestCount || undefined },
    ...(stockPlanningEnabled() ? [{ key: "stock" as TabKey, label: "STORAGE_TAB_STOCK" }] : []),
    { key: "tasks", label: "STORAGE_TAB_TASKS" },
    // Offered only to someone who may read access, so a viewer is not sent to a tab that can only
    // tell them no.
    ...(may(STORAGE_ACTIONS.AccessRead) ? [{ key: "access" as TabKey, label: "STORAGE_ACCESS" }] : []),
    { key: "activity", label: "STORAGE_TAB_ACTIVITY", count: activityCount() || undefined },
  ]);

  return (
    <div class={styles.container}>
      <div class={styles.headerRow}>
        <h1>
          <Text val="STORAGE_ADMIN" />
          <Show when={!loading()}><span class={styles.entryCount}>{storages().length}</span></Show>
        </h1>
        <button class="btn btn-primary" onClick={() => void handleCreate()}>
          <i class="fa-solid fa-plus" /> <Text val="STORAGE_NEW" />
        </button>
      </div>

      <Show when={!loading()} fallback={
        <div class={styles.layout}>
          <div class={styles.rail}><Skeleton variant="row" height="3rem" count={4} /></div>
          <div class={styles.detail}><Skeleton variant="rect" height="26rem" /></div>
        </div>
      }>
        <div class={styles.layout}>
          <StorageList storages={storages} selectedId={selectedId} onSelect={(id) => void handleSelect(id)} />

          <div class={styles.detail}>
            <Show when={selectedStorage()} fallback={
              <EmptyState illustration="inventory" title={t("STORAGE_SELECT_PROMPT")} />
            }>
              {(storage) => (
                <>
                  <div class={styles.dHead}>
                    <div class={styles.dTop}>
                      <div class={styles.dTitleWrap}>
                        <div class={styles.dTitleRow}>
                          <h2>{storage().name}</h2>
                          <span class={styles.ownerBadge}>
                            <Text val={storage().isOwner ? "STORAGE_OWNER" : "STORAGE_SHARED"} />
                          </span>
                          <Show when={storage().sellChamberBotId}>
                            <Badge tone="accent" icon="fa-store"><Text val="SELLBOT_CHAMBER" /></Badge>
                          </Show>
                        </div>
                        <div class={styles.dSub}>
                          <Show when={!storage().isOwner}>{storage().ownerName} <span class={styles.dot}>·</span> </Show>
                          {storage().citybuildName}
                          <Show when={storage().location}> <span class={styles.dot}>·</span> {storage().location}</Show>
                        </div>
                      </div>
                      <Show when={may(STORAGE_ACTIONS.ConfigWrite) && !editing()}>
                        <button class="btn btn-secondary btn-sm" onClick={() => { selectTab("overview"); setEditing(true); }}>
                          <i class="fa-solid fa-pen" /> <Text val="EDIT" />
                        </button>
                      </Show>
                    </div>

                    <div class={styles.tabs}>
                      <For each={tabs()}>
                        {(tab) => (
                          <button
                            class={`${styles.tab} ${activeTab() === tab.key ? styles.tabActive : ""}`}
                            onClick={() => selectTab(tab.key)}
                          >
                            <Text val={tab.label} />
                            <Show when={tab.count !== undefined}>
                              <span class={styles.tabCount}>{tab.count}</span>
                            </Show>
                          </button>
                        )}
                      </For>
                    </div>
                  </div>

                  <div class={styles.body}>
                    <Show when={activeTab() === "overview"}>
                      <StorageOverviewTab
                        storage={storage()}
                        assignedBots={assignedBots()}
                        editing={editing()}
                        onEditingChange={setEditing}
                        onStorageChanged={() => loadStorages()}
                        onBotsChanged={() => loadAssignedBots(storage().id)}
                        onDeleted={handleDeleted}
                      />
                    </Show>

                    <Show when={activeTab() === "chests"} keyed>
                      <StorageChestsTab
                        storage={storage()}
                        assignedBotIds={assignedBotIds()}
                        onChestsChanged={() => loadStorages()}
                      />
                    </Show>

                    <Show when={activeTab() === "stock"}>
                      <StorageStockTab storage={storage()} storages={storages()} />
                    </Show>

                    <Show when={activeTab() === "tasks"}>
                      <StorageTasksTab
                        storage={storage()}
                        assignedBotIds={assignedBotIds()}
                        canRunTasks={may(STORAGE_ACTIONS.TasksRun)}
                        onGoToOverview={() => selectTab("overview")}
                      />
                    </Show>

                    <Show when={activeTab() === "access"}>
                      <ResourceAccessPanel resourceId={storage().id} />
                    </Show>

                    <Show when={activeTab() === "activity"} keyed>
                      <StorageActivityTab storageId={storage().id} onCountChange={setActivityCount} />
                    </Show>
                  </div>
                </>
              )}
            </Show>
          </div>
        </div>
      </Show>
    </div>
  );
}
